import { useState, type FormEvent } from 'react';
import { Form } from '@beyond-js/ui/react';
import { useI18n } from '@cdn/i18n';
import { Button, Field, Notice, ProblemNotice, TextInput } from '@cdn/ui';
import { useSession } from './context';
import type { SignInRequest } from './port';

/**
 * The sign-in controls of the configured identity adapter. `done` runs once a credential was accepted; a
 * provider sign-in leaves the page instead and comes back through the return route.
 */
export function SignInForm({ done, leaving }: { done: () => void; leaving?: () => void }) {
	const { t } = useI18n();
	const session = useSession();
	const { identity } = session;
	const [email, setEmail] = useState('');
	const [name, setName] = useState('');
	const [password, setPassword] = useState('');
	const [busy, setBusy] = useState(false);
	const [problem, setProblem] = useState<unknown>();

	const run = async (request: SignInRequest) => {
		setBusy(true);
		setProblem(undefined);
		try {
			if (['github', 'google', 'platform'].includes(request.method)) leaving?.();
			if (request.method === 'platform') session.departures?.leave();
			(await session.signin(request)) && done();
		} catch (thrown) {
			setProblem(thrown);
		} finally {
			setBusy(false);
		}
	};

	const submit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		void run(identity.methods.includes('local') ? { method: 'local', email, name: name.trim() || undefined } : { method: 'password', email, password });
	};

	// The whole screen when the shared authority is this installation's identity: there is one way in,
	// and offering a second would be offering a second authority
	if (identity.methods.includes('platform')) {
		return (
			<div className="space-y-5">
				{problem !== undefined && <ProblemNotice problem={problem} />}
				<p className="text-muted">{t('identity.signin.platform.lead')}</p>
				<div className="grid">
					<Button variant="primary" busy={busy} onClick={() => void run({ method: 'platform' })}>
						{t('identity.signin.platform')}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-5">
			{!identity.validated && <Notice tone="warning">{t('identity.signin.unvalidated')}</Notice>}
			{problem !== undefined && <ProblemNotice problem={problem} />}

			{identity.methods.includes('github') && (
				<div className="grid gap-3">
					<Button variant="primary" busy={busy} onClick={() => void run({ method: 'github' })}>
						{t('identity.signin.github')}
					</Button>
					<Button busy={busy} onClick={() => void run({ method: 'google' })}>
						{t('identity.signin.google')}
					</Button>
					<p className="text-center text-sm text-muted">{t('identity.signin.or')}</p>
				</div>
			)}

			<Form report onSubmit={submit} className="space-y-4">
				<Field label={t('identity.signin.email')}>
					{control => <TextInput {...control} data-autofocus type="email" name="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} />}
				</Field>
				{identity.methods.includes('local') && (
					<Field label={t('identity.signin.name')} hint={t('identity.signin.name.hint')}>
						{control => <TextInput {...control} name="name" autoComplete="name" value={name} onChange={event => setName(event.target.value)} />}
					</Field>
				)}
				{identity.methods.includes('password') && (
					<Field label={t('identity.signin.password')}>
						{control => <TextInput {...control} type="password" name="password" autoComplete="current-password" required value={password} onChange={event => setPassword(event.target.value)} />}
					</Field>
				)}
				<div className="grid">
					<Button type="submit" variant={identity.methods.includes('github') ? 'secondary' : 'primary'} busy={busy}>
						{t('identity.signin.submit')}
					</Button>
				</div>
			</Form>
			{identity.methods.includes('local') && <p className="text-sm text-muted">{t('identity.signin.local')}</p>}
		</div>
	);
}
